import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';

import type { Style } from '../component/elements/StyleJigsaw';
import { newStyles } from '../component/elements/StyleJigsaw';
import { logEvent } from '../lib/analytics';
import type { AudioSource } from '../lib/api';
import { requestAudioAPI, setupProgressiveMediaSource } from '../lib/api';
import { config } from '../lib/config';
import { AUDIO_GENERATION_LIMIT } from '../lib/constants';
import type { Prosody, VoiceDataType } from '../lib/types';
import { PLAY_STATE } from '../lib/types';
import {
  debounce,
  generateSHA256,
  getEmotionFromStyle,
  getStaticAudioFileName,
  textToSSML,
} from '../lib/utils';
import { useVoices } from '../providers/VoicesProvider';

export const numGenerationKey = 'num_generation';
const AUDIO_CACHE_KEY = 'audio_generation_cache';
const SILENT_WAV = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQQAAAAAAA==';

interface CachedAudio {
  playbackUrl: string;
  downloadUrl: string;
  timestamp: number;
}

interface Error {
  message: string;
  type: 'rateLimit' | 'general';
}

async function generateContentHash(
  voiceName: string,
  languageCode: string,
  emotion: string | null,
  content: string,
): Promise<string> {
  const input = `${voiceName}|${languageCode}|${emotion ?? 'null'}|${content}`;
  const hash = await generateSHA256(input);
  return `audio_${hash}`;
}

/**
 * Verifies if a blob URL is still valid by attempting to fetch it
 */
async function isBlobUrlValid(url: string): Promise<boolean> {
  if (!url.startsWith('blob:')) {
    return true;
  }
  
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Gets cached audio from localStorage if it exists
 */
function getCachedAudio(hash: string): CachedAudio | null {
  try {
    const cache = localStorage.getItem(AUDIO_CACHE_KEY);
    if (!cache) return null;
    
    const cacheMap = JSON.parse(cache) as Record<string, CachedAudio>;
    return cacheMap[hash] || null;
  } catch {
    return null;
  }
}

/**
 * Stores audio URLs in localStorage cache
 */
function setCachedAudio(hash: string, audio: CachedAudio): void {
  try {
    const cache = localStorage.getItem(AUDIO_CACHE_KEY);
    const cacheMap = cache ? (JSON.parse(cache) as Record<string, CachedAudio>) : {};
    cacheMap[hash] = audio;
    localStorage.setItem(AUDIO_CACHE_KEY, JSON.stringify(cacheMap));
  } catch (error) {
    console.warn('Failed to cache audio:', error);
  }
}

const getRecommendedModel = (models: string[], languageCode: string) => {
  if (languageCode.startsWith('en') && models.includes('simba-english'))
    return 'simba-english';
  else if (models.includes('simba-multilingual')) {
    return 'simba-multilingual';
  } else {
    return 'simba-base';
  }
};

export const useAudioPlayer = (
  currentContent: string,
  currentStyle: Style | null,
  prosody: Prosody,
  currentVoice: VoiceDataType,
) => {
  const generations = Number(Cookies.get(numGenerationKey));
  const [playerState, setPlayerState] = useState<PLAY_STATE>(
    PLAY_STATE.STOPPED,
  );
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const [audioDataUrl, setAudioDataUrl] = useState<string>();
  const [audioDownloadUrl, setAudioDownloadUrl] = useState<string>();
  const [showReplay, setShowReplay] = useState(false);
  const [isContentChanged, setIsContentChanged] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [showUpsell, setShowUpsell] = useState(false);
  const { staticAudioFilesManifest } = useVoices();
  const [numGeneration, setNumGeneration] = useState<number>(
    isNaN(generations) ? 0 : generations,
  );

  const requestIdRef = useRef(0);
  
  // Track streaming audio context and sources for proper cleanup
  const audioContextRef = useRef<AudioContext | null>(null);
  const scheduledSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const streamReaderRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const hasStartedPlayingRef = useRef<boolean>(false);


  const clearErrorToast = () => {
    setTimeout(() => {
      setError(null);
    }, 3000);
  };

  const cleanupAudioListeners = (audio: HTMLAudioElement) => {
    audio.pause();
    audio.currentTime = 0;
    audio.onended = null;
    audio.onloadedmetadata = null;
    audio.onerror = null;
    audio.onplaying = null;
  };

  const cleanupStreamingAudio = () => {
    // Stop all scheduled audio sources
    scheduledSourcesRef.current.forEach((source) => {
      try {
        source.stop();
        source.disconnect();
      } catch {
        // Source may have already finished or been stopped
      }
    });
    scheduledSourcesRef.current = [];

    // Close and cleanup AudioContext
    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => {
        // Ignore close errors
      });
      audioContextRef.current = null;
    }

    // Cancel stream reading
    if (streamReaderRef.current) {
      try {
        void streamReaderRef.current.cancel();
      } catch {
        // Reader may already be released
      }
      streamReaderRef.current = null;
    }
    hasStartedPlayingRef.current = false;
  };

  const playText = async () => {
    requestIdRef.current += 1;
    const thisRequestId = requestIdRef.current;
    const synthesisStartTime = Date.now();
    logEvent('speech_synthesis_started', {
      voice: currentVoice.name,
      language: currentVoice.languageCode,
      content_length: currentContent.length,
      style: currentStyle,
    });
    setPlayerState(PLAY_STATE.LOADING);
    cleanupStreamingAudio();

    try {
      const audioResult = await loadAudioDataUrl(thisRequestId);
      
      // Only play if this is still the latest request
      if (audioResult && thisRequestId === requestIdRef.current) {
        if (audioResult.type === 'stream') {
          const stream = await audioResult.createStream();
          void playTtsStreamFromReadable(stream, synthesisStartTime);
        } else {
          playAudio(audioResult.playbackUrl, synthesisStartTime);
        }
      }
    } catch (error) {
      setPlayerState(PLAY_STATE.STOPPED);
      if (error instanceof Error && error.message.includes('limit exceeded')) {
        setShowUpsell(true);
        setPlayerState(PLAY_STATE.STOPPED);
      } else {
        setError({
          type: 'general',
          message: 'An error occurred. Please try again later.',
        });
        clearErrorToast();
      }
    }
  };

  const loadAudioDataUrlInternal = useCallback(async (requestId: number): Promise<AudioSource | undefined> => {
    if (requestId !== requestIdRef.current) {
      return undefined;
    }

    const emotion = getEmotionFromStyle();
    
    const contentHash = await generateContentHash(
      currentVoice.name,
      currentVoice.languageCode,
      emotion ?? null,
      currentContent,
    );

    const cachedAudio = getCachedAudio(contentHash);
    if (cachedAudio) {
      const isPlaybackValid = await isBlobUrlValid(cachedAudio.playbackUrl);
      const isDownloadValid = await isBlobUrlValid(cachedAudio.downloadUrl);
      
      if (isPlaybackValid && isDownloadValid) {
        if (requestId === requestIdRef.current) {
          setAudioDataUrl(cachedAudio.playbackUrl);
          setAudioDownloadUrl(cachedAudio.downloadUrl);
          return {
            type: 'url',
            playbackUrl: cachedAudio.playbackUrl,
            downloadUrl: cachedAudio.downloadUrl,
          };
        }
        return undefined;
      }
    }

    const cachedAudioUrl = await getStaticAudioUrl(
      currentVoice.name,
      currentVoice.languageCode,
      emotion ?? null,
      currentContent,
    );

    if (staticAudioFilesManifest.includes(cachedAudioUrl.split('/').pop()!)) {
      if (requestId === requestIdRef.current) {
        setAudioDataUrl(cachedAudioUrl);
        setAudioDownloadUrl(cachedAudioUrl);
        setCachedAudio(contentHash, {
          playbackUrl: cachedAudioUrl,
          downloadUrl: cachedAudioUrl,
          timestamp: Date.now(),
        });
        return {
          type: 'url',
          playbackUrl: cachedAudioUrl,
          downloadUrl: cachedAudioUrl,
        };
      }
      return undefined;
    }

    const isRegeneration = cachedAudio !== null;
    if (!isRegeneration && numGeneration >= AUDIO_GENERATION_LIMIT) {
      setPlayerState(PLAY_STATE.STOPPED);
      return undefined;
    }

    if (!isRegeneration) {
      Cookies.set(numGenerationKey, String(numGeneration + 1));
      setNumGeneration(numGeneration + 1);
    }
    
    const inputSSML = textToSSML(currentContent, {
      emotion,
    });
    const styleDetail = currentStyle ? newStyles.find((s) => s.style === currentStyle) : null;
    const prompt = styleDetail?.prompt;
    const audioResult = await requestAudioAPI(
      {
        input: inputSSML,
        voice_id: currentVoice.name,
        model: getRecommendedModel(
          currentVoice.models,
          currentVoice.languageCode,
        ),
        language: currentVoice.languageCode,
        ...(prompt && { prompt }),
      },
    );
    
    if (requestId === requestIdRef.current) {
      if (audioResult.type === 'url') {
        setAudioDataUrl(audioResult.playbackUrl);
        setAudioDownloadUrl(audioResult.downloadUrl);
        
        setCachedAudio(contentHash, {
          playbackUrl: audioResult.playbackUrl,
          downloadUrl: audioResult.downloadUrl,
          timestamp: Date.now(),
        });
      }
      
      return audioResult;
    }
    return undefined;
  }, [
    currentVoice,
    currentContent,
    currentStyle,
    numGeneration,
    staticAudioFilesManifest,
  ]);

  const loadAudioDataUrl = useMemo(
    () => debounce(loadAudioDataUrlInternal, 500),
    [loadAudioDataUrlInternal]
  );

  const handlePlayText = () => {
    if (!currentContent) return null;
    audioRef.current.src = SILENT_WAV;
    audioRef.current.play().catch(() => {});

    const currentCount = 0;
    if (currentCount < 3) {
      playText().catch((err: Error) => {
        throw new Error(err.message);
      });
    } else {
      setShowUpsell(true);
      return null;
    }
  };

  const handlePlaybackEnd = () => {
    setPlayerState(PLAY_STATE.STOPPED);
    setShowReplay(true);
  };

  const stopAndReset = () => {
    if (audioRef.current) {
      cleanupAudioListeners(audioRef.current);
    }
    cleanupStreamingAudio();
    setAudioDataUrl(undefined);
    setAudioDownloadUrl(undefined);
    setPlayerState(PLAY_STATE.STOPPED);
    setShowReplay(false);
    setIsContentChanged(false);
  };

  const playAudio = (audioUrl: string, synthesisStartTime: number) => {
    const audio = audioRef.current;
    cleanupAudioListeners(audio);
    
    audio.src = audioUrl;
    audio.playbackRate = prosody.rate;

    audio.onended = () => handlePlaybackEnd();
    
    audio.onplaying = () => setPlayerState(PLAY_STATE.PLAYING);

    audio.onloadedmetadata = () => {
      logEvent('speech_playback_started', {
        synthesisTimeInMs: Date.now() - synthesisStartTime,
        voice: currentVoice.name,
        language: currentVoice.languageCode,
        content_length: currentContent.length,
        style: currentStyle,
        audio_duration_seconds: audio.duration,
      });
    };

    audio.onerror = () => {
      if (audio.readyState === 0 && audio.networkState === 3) return;
      setPlayerState(PLAY_STATE.STOPPED);
      setError({ type: 'general', message: 'Failed to load audio. Please try again.' });
      clearErrorToast();
    };

    audio.play().catch((err) => {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('[AudioPlayer] Play error:', err);
      setPlayerState(PLAY_STATE.STOPPED);
    });
  };

  const playTtsStreamFromReadable = async (
    stream: ReadableStream<Uint8Array>,
    synthesisStartTime: number,
  ) => {
    try {
      cleanupStreamingAudio();

      if (audioDataUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(audioDataUrl);
      }
      if (audioDownloadUrl !== audioDataUrl && audioDownloadUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(audioDownloadUrl);
      }

      const audio = audioRef.current;
      cleanupAudioListeners(audio);
      audio.playbackRate = prosody.rate;

      audio.onplaying = () => {
        if (!hasStartedPlayingRef.current) {
          hasStartedPlayingRef.current = true;
          setPlayerState(PLAY_STATE.PLAYING);
          logEvent('speech_playback_started', {
            synthesisTimeInMs: Date.now() - synthesisStartTime,
            voice: currentVoice.name,
            language: currentVoice.languageCode,
            content_length: currentContent.length,
            style: currentStyle,
            audio_duration_seconds: audio.duration || 0,
          });
        }
      };

      audio.onended = () => {
        handlePlaybackEnd();
      };

      audio.onerror = (e) => {
        console.error('Audio stream error:', e, audio.error);
        setPlayerState(PLAY_STATE.STOPPED);
        setError({
          type: 'general',
          message: 'Failed to play audio stream. Please try again.',
        });
        clearErrorToast();
      };

      const playbackUrl = await setupProgressiveMediaSource(stream, (audioUrl) => {
        if (audio.src !== audioUrl) {
          audio.src = audioUrl;
        }
        audio.play().catch((err) => {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          console.error('Play error:', err);
          setPlayerState(PLAY_STATE.STOPPED);
          setError({
            type: 'general',
            message: 'Failed to start playback. Please try again.',
          });
          clearErrorToast();
        });
      });

      if (audio.src !== playbackUrl) {
        audio.src = playbackUrl;
      }

      setAudioDataUrl(playbackUrl);
      setAudioDownloadUrl(playbackUrl);

    } catch (error) {
      console.error('Audio stream error:', error);
      cleanupStreamingAudio();
      setPlayerState(PLAY_STATE.STOPPED);
      setError({
        type: 'general',
        message:
          error instanceof Error && error.name === 'AbortError'
            ? 'Audio stream timed out. Please try again.'
            : error instanceof Error
              ? error.message
              : 'Failed to stream audio. Please try again.',
      });
      clearErrorToast();
    }
  };

  const togglePlayback = () => {
    setShowReplay(false);
    
    // Check if audio is currently playing (either via audioRef or streaming)
    if (playerState === PLAY_STATE.PLAYING) {
      // Stop playback
      if (audioRef.current) {
        audioRef.current.pause();
      }
      // Also cleanup any streaming audio
      cleanupStreamingAudio();
      setPlayerState(PLAY_STATE.STOPPED);
      return;
    }
    
    // If we have a paused audio element, resume it
    const isPlaying = !audioRef?.current?.paused && !audioRef?.current?.ended;
    if (audioRef.current && audioDataUrl && !isPlaying) {
      audioRef.current.play().catch((err: Error) => {
        throw new Error(err.message);
      });
      setPlayerState(PLAY_STATE.PLAYING);
      return;
    }
    
    // Otherwise, start new playback
    handlePlayText();
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        cleanupAudioListeners(audioRef.current);
      }
      cleanupStreamingAudio();
    };
  }, []);

  return {
    playAudio,
    stopAndReset,
    togglePlayback,
    playerState,
    showReplay,
    audioRef,
    error,
    setIsContentChanged,
    isContentChanged,
    showUpsell,
    setShowUpsell,
    setPlayerState,
    audioDataUrl,
    audioDownloadUrl,
    numGeneration,
    loadAudioDataUrl,
    requestIdRef,
  };
};
async function getStaticAudioUrl(
  voiceId: string,
  locale: string,
  emotion: string | null,
  text: string,
): Promise<string> {
  const fileName = await getStaticAudioFileName(
    voiceId,
    locale,
    emotion?.toLowerCase() ?? 'neutral',
    text,
  );
  return `${config.static.public_base_url}/assets/audio/${fileName}`;
}

